/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Button, FlexColumn, FlexRow, Typography } from 'design-system';
import { ArrowRightIcon, LockIcon, SpinnerIcon } from 'design-system/components/Icons';
import QuizCardShell, { QuizPassMarkNote } from '../../components/QuizCardShell';
import GuidanceStepHeader from '../../components/GuidanceStepHeader';
import QuizProgressSegments from '../../components/QuizProgressSegments';

export default function QuizLoadingState({ course, passMarkPercent, questionCount }) {
  const intl = useIntl();
  const totalQuestions = questionCount > 0 ? questionCount : 1;
  const quizTitle = intl.formatMessage(
    { id: 'ld-knowledge-check-title' },
    { title: course?.title ?? intl.formatMessage({ id: 'ld-knowledge-check-subtitle' }) },
  );

  const footerStart = (
    <FlexRow align="center" fullWidth={false} gap="var(--ds-space-2)">
      <LockIcon size={16} className="ds-icon--color-muted" aria-hidden />
      <Typography component="span" variant="bodySmall" color="ui-subtle">
        {intl.formatMessage({ id: 'ld-preparing-questions' })}
      </Typography>
    </FlexRow>
  );

  const footerAction = (
    <Button
      variant="primary"
      size="base"
      className="ld-guidance-step__continue"
      icon={<ArrowRightIcon size={11} />}
      iconPosition="right"
      disabled
    >
      {intl.formatMessage({ id: 'next-question' })}
    </Button>
  );

  return (
    <FlexColumn align="stretch" fullWidth gap="var(--ds-space-6)" className="ld-quiz">
      <GuidanceStepHeader
        label={intl.formatMessage({ id: 'ld-knowledge-check' })}
        title={quizTitle}
        instruction={
          questionCount > 0
            ? intl.formatMessage(
              { id: 'ld-quiz-instruction' },
              { count: totalQuestions, passMark: passMarkPercent },
            )
            : intl.formatMessage(
              { id: 'ld-quiz-instruction-pass-only' },
              { passMark: passMarkPercent },
            )
        }
      />

      <QuizProgressSegments total={totalQuestions} activeIndex={0} />

      <QuizCardShell
        questionLabel={intl.formatMessage(
          { id: 'ld-question-of' },
          { current: 1, total: totalQuestions },
        )}
        footerStart={footerStart}
        footerAction={footerAction}
        busy
      >
        <FlexColumn
          align="center"
          justify="center"
          fullWidth
          gap="var(--ds-space-3)"
          className="ld-quiz__loading"
        >
          <SpinnerIcon
            size={24}
            className="ds-icon--spin"
            aria-hidden
            trackFill="var(--ds-theme-loader-track-fill)"
            arcFill="var(--ds-theme-action-primary)"
            arc2Fill="var(--ds-theme-action-primary)"
          />
          <Typography component="p" variant="bodyMedium" color="ui-subtle">
            {intl.formatMessage({ id: 'ld-loading-questionnaire' })}
          </Typography>
        </FlexColumn>
      </QuizCardShell>

      <QuizPassMarkNote passMarkPercent={passMarkPercent} />
    </FlexColumn>
  );
}

QuizLoadingState.propTypes = {
  course: PropTypes.shape({
    title: PropTypes.string,
    questionCount: PropTypes.number,
  }),
  passMarkPercent: PropTypes.number.isRequired,
  questionCount: PropTypes.number,
};
